import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { BlogPostGrid } from "@/components/site/BlogPostGrid";
import { seoMeta, canonicalLink, absoluteUrl, robotsMeta } from "@/lib/seo";
import { SITE_URL } from "@/lib/site";
import { CATEGORY_SLUGS, CATEGORY_META, getCategoryBySlug } from "@/lib/blog";
import { getPublishedPostsByCategory } from "@/server/blog/blog-public-detail.fns";

export const Route = createFileRoute("/blog/categoria/$category")({
  loader: async ({ params }) => {
    const category = getCategoryBySlug(params.category);
    if (!category) throw notFound();
    const posts = await getPublishedPostsByCategory({ data: { category } });
    return { category, posts };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-5 py-32 text-center md:px-8">
        <h1 className="font-display text-4xl font-bold text-secondary">Categoría no encontrada</h1>
        <p className="mt-4 text-secondary/70">La categoría que buscas no existe.</p>
        <Link
          to="/blog"
          className="mt-8 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
        >
          Ver todos los artículos
        </Link>
      </div>
    </PageShell>
  ),
  head: ({ loaderData, match }) => {
    if (!loaderData) return {};
    const { category } = loaderData;
    const meta = CATEGORY_META[category];
    const slug = CATEGORY_SLUGS[category];
    const path = `/blog/categoria/${slug}`;
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? meta.title,
          description: override?.metaDescription ?? meta.description,
          path,
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? path),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: absoluteUrl("/blog") },
              { "@type": "ListItem", position: 3, name: category, item: absoluteUrl(path) },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": absoluteUrl(path),
            name: meta.title,
            description: meta.description,
            url: absoluteUrl(path),
            inLanguage: "es-PE",
            isPartOf: {
              "@type": "Blog",
              "@id": absoluteUrl("/blog"),
              name: "Blog Improve Yourself",
            },
          }),
        },
      ],
    };
  },
  component: BlogCategoryPage,
});

function BlogCategoryPage() {
  const { category, posts } = Route.useLoaderData();
  const meta = CATEGORY_META[category];

  // Solo las guías publicadas de esta categoría: un slug retirado o movido no deja un enlace roto.
  const porSlug = new Map(posts.map((p) => [p.slug, p]));
  const grupos = meta.grupos
    .map((g) => ({
      titulo: g.titulo,
      guias: g.slugs.flatMap((slug) => {
        const post = porSlug.get(slug);
        return post ? [post] : [];
      }),
    }))
    .filter((g) => g.guias.length > 0);

  return (
    <PageShell>
      <PageHero eyebrow={`· ${category}`} h1={meta.h1} intro={meta.intro} />
      <section className="pb-14">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <div className="space-y-4 text-base leading-relaxed text-secondary/80">
            {meta.texto.map((parrafo) => (
              <p key={parrafo}>{parrafo}</p>
            ))}
          </div>
          {grupos.length > 0 && (
            <div className="mt-10">
              <h2 className="font-display text-2xl font-bold text-secondary md:text-3xl">
                Por dónde empezar
              </h2>
              <div className="mt-6 space-y-8">
                {grupos.map((g) => (
                  <div key={g.titulo}>
                    <h3 className="font-display text-lg font-bold text-secondary">{g.titulo}</h3>
                    <ul className="mt-3 space-y-3">
                      {g.guias.map((post) => (
                        <li key={post.slug}>
                          <Link
                            to="/blog/$slug"
                            params={{ slug: post.slug }}
                            className="font-semibold text-primary hover:underline"
                          >
                            {post.h1}
                          </Link>
                          <p className="mt-0.5 text-sm text-secondary/65">{post.excerpt}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <BlogPostGrid posts={posts} activeCategory={category} />
        </div>
      </section>
    </PageShell>
  );
}
